import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../layout/PageLayout';
import { useModule } from '../../contexts/ModuleContext';
import { fetchProjects } from '../../services/projectService';
import { fetchSurveys } from '../../services/projectSurveyService';
import { Building2, ChevronDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export default function ConstructionLayout({ title, subtitle, children, showProjectSelector = true }) {
  const { activeProject, setActiveProject, activeSurvey, setActiveSurvey, surveyDateLabel } = useModule();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [surveyDropdownOpen, setSurveyDropdownOpen] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [surveysList, setSurveysList] = useState([]);

  // Other construction pages require an explicitly selected project
  useEffect(() => {
    if (showProjectSelector && !activeProject?.id) {
      navigate('/construction/projects', { replace: true });
    }
  }, [showProjectSelector, activeProject, navigate]);

  useEffect(() => {
    // Projects page loads its own list; skip duplicate /projects fetch here.
    if (!showProjectSelector) {
      setProjectsList([]);
      return undefined;
    }
    let isMounted = true;
    fetchProjects()
      .then((data) => {
        if (isMounted && data) {
          setProjectsList(data);
        }
      })
      .catch((err) => console.error('Error fetching project dropdown list:', err));

    return () => {
      isMounted = false;
    };
  }, [showProjectSelector]);

  // Load surveys for active project — drives Last Survey dropdown
  useEffect(() => {
    let isMounted = true;
    const projectKey = activeProject?.numericId || activeProject?.code || activeProject?.id;
    if (!projectKey) {
      setSurveysList([]);
      return undefined;
    }

    fetchSurveys(projectKey)
      .then((rows) => {
        if (!isMounted) return;
        setSurveysList(rows);
        if (!rows.length) {
          setActiveSurvey(null);
          return;
        }
        const stillValid = activeSurvey?.id && rows.some((s) => s.id === activeSurvey.id);
        if (!stillValid) {
          setActiveSurvey(rows[0]);
        }
      })
      .catch((err) => console.error('Error fetching surveys:', err));

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fetch when project identity changes
  }, [activeProject?.numericId, activeProject?.code, activeProject?.id]);

  const availableSurveys = surveysList;

  const getSubtitle = () => {
    if (subtitle) return subtitle;
    switch ((title || '').toLowerCase()) {
      case 'earthwork':
        return 'Excavation and cut/fill analysis from drone surveys';
      case 'stockpiles':
        return 'Inventory and movement analysis from drone surveys';
      case 'safety observations':
      case 'site observations':
        return 'Site issues and observations captured during drone survey';
      case 'site visuals':
        return 'High-resolution orthomosaic and 3D elevation maps';
      case 'progress':
        return 'Overall construction progress tracking and variance analysis';
      case 'reports':
        return 'Generated survey reports and site audit documentation';
      default:
        return 'Select a project to view intelligence and analytics';
    }
  };

  return (
    <PageLayout title={title}>
      <div className="construction-container" style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {showProjectSelector && activeProject?.id && (
          <div
            className="construction-top-bar"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <h2 style={{ fontSize: '22px', fontWeight: '700', color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>
                {title}
              </h2>
              <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: '4px 0 0 0' }}>
                {getSubtitle()}
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: isDarkMode ? '#111622' : '#FFFFFF',
                border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`,
                borderRadius: '10px',
                padding: '4px 8px',
                position: 'relative',
              }}
            >
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    setProjectDropdownOpen((prev) => !prev);
                    setSurveyDropdownOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'none',
                    border: 'none',
                    color: isDarkMode ? '#F3F4F6' : '#111827',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: '6px 10px',
                    borderRadius: '6px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      backgroundColor: isDarkMode ? '#1E2433' : '#E0E7FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Building2 size={16} color="#0082F8" />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                      {activeProject?.name || 'Select Project'}
                    </div>
                    <div style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      {activeProject?.location || ''}
                    </div>
                  </div>
                </button>

                {projectDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      width: '240px',
                      backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
                      border: `1px solid ${isDarkMode ? '#252D3D' : '#E5E7EB'}`,
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                      zIndex: 100,
                      padding: '6px',
                    }}
                  >
                    <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                      Select Project
                    </div>
                    {projectsList.map((proj) => (
                      <button
                        key={proj.id}
                        onClick={() => {
                          const code = proj.code || proj.id;
                          setActiveProject({
                            id: code,
                            code,
                            numericId: proj.numericId,
                            name: proj.name,
                            location: proj.location,
                            lastSurvey: proj.lastSurveyDate ? proj.lastSurvey : null,
                            imageUrl: proj.imageUrl || null,
                          });
                          setProjectDropdownOpen(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: activeProject?.id === proj.id ? (isDarkMode ? '#222938' : '#EFF6FF') : 'transparent',
                          color: isDarkMode ? '#F3F4F6' : '#111827',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{proj.name}</div>
                        <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{proj.location}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ width: '1px', height: '28px', backgroundColor: isDarkMode ? '#1E2433' : '#E5E7EB', margin: '0 6px' }} />

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    setSurveyDropdownOpen((prev) => !prev);
                    setProjectDropdownOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'none',
                    border: 'none',
                    color: isDarkMode ? '#F3F4F6' : '#111827',
                    cursor: 'pointer',
                    textAlign: 'left',
                    padding: '6px 10px',
                    borderRadius: '6px',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '10px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                      Last Survey
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                      {surveyDateLabel || 'No surveys yet'}
                      <ChevronDown size={14} style={{ color: '#9CA3AF' }} />
                    </div>
                  </div>
                </button>

                {surveyDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      width: '230px',
                      backgroundColor: isDarkMode ? '#161B26' : '#FFFFFF',
                      border: `1px solid ${isDarkMode ? '#252D3D' : '#E5E7EB'}`,
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                      zIndex: 100,
                      padding: '6px',
                    }}
                  >
                    <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>
                      Switch Survey Report
                    </div>
                    {availableSurveys.length === 0 && (
                      <div style={{ padding: '8px 10px', fontSize: '12px', color: '#9CA3AF' }}>No surveys yet</div>
                    )}
                    {availableSurveys.map((srv) => (
                      <button
                        key={srv.id || srv.dateLabel}
                        onClick={() => {
                          setActiveSurvey(srv);
                          setSurveyDropdownOpen(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: activeSurvey?.id === srv.id || activeSurvey?.dateLabel === srv.dateLabel
                            ? (isDarkMode ? '#222938' : '#EFF6FF')
                            : 'transparent',
                          color: isDarkMode ? '#F3F4F6' : '#111827',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: activeSurvey?.id === srv.id ? 700 : 400,
                        }}
                      >
                        {srv.dateLabel}
                        {srv.latest ? ' (Latest)' : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="construction-content" style={{ flex: 1 }}>
          {children}
        </div>
      </div>
    </PageLayout>
  );
}
